import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { JS0N } from './ui/LoadingIndicator';
import { Card, Button, Container, Row, Col } from 'react-bootstrap';
import { useTheme } from '../ThemeContext';
import useSound from 'use-sound';
import { debounce } from 'lodash';
import { useFilter } from './FilterContext';
import { useNavigate } from 'react-router-dom';
import { hideGlassToast, showGlassToast } from './ui/GlassToast';
import { WS_URL } from '../config/endpoints';

const WebSocketContext = createContext(null);
const WS_CONNECTION_TOAST_ID = 'ws-connection-status';

const calculateBenefit = (fp, p) => Math.round((fp / p) * 100);

const filterSales = (sales, filters) => {
  return sales.filter(sale => {
    // 1. Поиск фильтра
    const f = filters.find(filter =>
      (sale.marketplace === 'wb' && filter.categ === sale.subject) ||
      (['ozon', 'yandex', 'detmir'].includes(sale.marketplace) &&
        filter.marketplace === sale.marketplace)
    );
    if (!f || !f.status_categ) return false;

    // 2. Расчёт выгоды от отзывов
    const fp = sale.feedbackpoints ?? 0;
    const p = sale.price;
    const benefitPercent = fp > 0 ? calculateBenefit(fp, p) : 0;

    /* 3. «отзывной» блок — если >= 50 % */

    if (fp > 0 && benefitPercent >= 50) {
      if (f.status_feedbacks) {
        /* 3.а. нужно ещё пройти пользовательский порог */
        if (benefitPercent < f.feedbackminpercent) return false;

        /* 3.б. возвратность */
        if (sale.isunrefusable && !f.unrefusableallowed) return false;

        /* 3.в. остаток и склад (WB) */
        if (sale.marketplace === 'wb') {
          if (sale.col < f.ostatok) return false;
          const sklad = sale.sklad2 ?? sale.sklad ?? '';
          const hasWB = /WB - (\d+)шт\./.test(sklad);
          const hasPR = /ПРОД - (\d+)шт\./.test(sklad);
          if ((!hasWB && hasPR && !f.prod) || (!hasWB && !hasPR)) return false;
        }
        /* ничего не проверяем по скидке/AI */
        return true;
      }
    }


    // 4. Обычная фильтрация по скидке
    let isDiscountMatch = false;
    if (sale.marketplace === 'wb') {
      isDiscountMatch = f.sale === 0
        ? true
        : sale.diapazmin && sale.price <= sale.diapazmin * (1 - f.sale / 100);
    } else {
      isDiscountMatch = f.sale === 0
        ? true
        : sale.oldprice && sale.price <= sale.oldprice * (1 - f.sale / 100);
    }
    if (!isDiscountMatch) return false;

    // 5. Доп. проверки для WB: AI, остаток, склад
    if (sale.marketplace === 'wb') {
      if (sale.ai < f.ai) return false;
      if (sale.col < f.ostatok) return false;
      const sklad = sale.sklad || '';
      const hasWB = /WB - (\d+)шт\./.test(sklad);
      const hasPR = /ПРОД - (\d+)шт\./.test(sklad);
      if ((!hasWB && hasPR && !f.prod) || (!hasWB && !hasPR)) return false;
    }

    return true;
  });
};
export const WebSocketProvider = ({ children }) => {
  const { theme } = useTheme();
  const { token, userInfo, isSoundEnabled, setIsLoading, logout, getFilters, filters, getFeedbackSales, getWsTicket } = useAuth();
  const { showFeedbackOnly } = useFilter();
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const hiddenCloseTimeout = useRef(null);
  const messageListeners = useRef(new Set());
  const reconnectAttempts = useRef(0);
  const shouldReconnectRef = useRef(true);
  const playSoundRef = useRef(null);
  const [isConnected, setIsConnected] = useState(null);
  const [shouldReconnect, setShouldReconnect] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [connectionEpoch, setConnectionEpoch] = useState(0);
  const [subscriptionEnded, setSubscriptionEnded] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const isFetchingRef = useRef(false);
  const [sales, setSales] = useState([]);
  const [feedbackSales, setFeedbackSales] = useState([]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const feedbackFetchingRef = useRef(false);
  const [play] = useSound('/notifsound.mp3');
  const salesRef = useRef([]);
  const filtersRef = useRef(filters);
  const isSoundEnabledRef = useRef(isSoundEnabled);
  const getFiltersRef = useRef(getFilters);
  const getWsTicketRef = useRef(getWsTicket);
  const logoutRef = useRef(logout);
  const filtersLoaded = useRef(false);

  useEffect(() => {
    salesRef.current = sales;
  }, [sales, userInfo?.status]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    isSoundEnabledRef.current = isSoundEnabled;
  }, [isSoundEnabled]);

  useEffect(() => {
    getFiltersRef.current = getFilters;
  }, [getFilters]);

  useEffect(() => {
    getWsTicketRef.current = getWsTicket;
  }, [getWsTicket]);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    shouldReconnectRef.current = shouldReconnect;
  }, [shouldReconnect]);

  useEffect(() => {
    playSoundRef.current = play;
  }, [play]);

  useEffect(() => {
    if (userInfo?.status !== false) return;

    setSales([]);
    setFeedbackSales([]);
    setSubscriptionEnded(false);
    setIsConnected(false);
    setConnectionStatus('disconnected');
    localStorage.removeItem('cachedSales');

    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      try {
        ws.current.close(1008, 'subscription is not active');
      } catch (ex) {
        console.warn('Failed to close ws for inactive subscription', ex);
      }
    }
  }, [userInfo?.status]);

  useEffect(() => {
    const fetchFilters = async () => {
      if (!token || userInfo?.status !== true) {
        filtersLoaded.current = false;
        setFiltersReady(false);
        return;
      }

      try {
        await getFiltersRef.current();  // Загружаем фильтры
        filtersLoaded.current = true;
        setFiltersReady(true);
        console.log('Фильтры успешно загружены:', filtersRef.current);  // Убедимся, что фильтры загрузились
      } catch (error) {
        filtersLoaded.current = false;
        setFiltersReady(false);
        console.error('Ошибка при получении фильтров:', error);
      }
    };

    void fetchFilters();
  }, [token, userInfo?.status]);

  useEffect(() => {
    const connectWebSocket = async () => {
      if (!token || !userInfo?.status || !filtersLoaded.current) {
        console.log('Нет токена, информации о пользователе или фильтров.');
        return;
      }

      if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
        console.log('WebSocket already open or connecting.');
        return;
      }

      setShouldReconnect(true);
      shouldReconnectRef.current = true;

      let wsUrl = '';
      try {
        const ticketResp = await getWsTicketRef.current();
        const ticket = String(ticketResp?.ticket || '').trim();
        if (ticket) {
          wsUrl = `${WS_URL}/ws/app?ticket=${encodeURIComponent(ticket)}`;
        }
      } catch (ticketError) {
        console.warn('WS ticket unavailable, fallback to legacy auth:', ticketError);
      }

      if (!wsUrl) {
        const mishura = encodeURIComponent(JS0N.stringify(token));
        const accountId = encodeURIComponent(String(userInfo.user_id));
        wsUrl = `${WS_URL}/ws/app?mishura=${mishura}&user_id=${accountId}`;
      }

      if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
        console.log('WebSocket already open or connecting.');
        return;
      }

      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log(`WebSocket connected`);

        setIsConnected(true);
        setShouldReconnect(true);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0;
        setConnectionEpoch((value) => value + 1);
        setSubscriptionEnded(false);

        const lastSale = salesRef.current[salesRef.current.length - 1];
        const lastSaleId = lastSale?.id;

        if (lastSaleId) {
          console.log(`Requesting new sales from ${lastSaleId}`);
          ws.current.send(JSON.stringify({ type: 'getNewSales', id: lastSaleId }));
        } else {
          console.log('Requesting last sales');
          ws.current.send(JSON.stringify({ type: 'getLastSales' }));
        }

      };

      ws.current.onmessage = (message) => {
        const data = JSON.parse(message.data);

        messageListeners.current.forEach((listener) => {
          try {
            listener(data);
          } catch (error) {
            console.error('WebSocket listener error:', error);
          }
        });

        if (data.type === 'sale' && data.dataApp) {
          console.log(data);
          const filtered = filterSales(data.dataApp, filtersRef.current || []);

          setSales((prevSales) => [...prevSales, ...filtered]);

          if (isSoundEnabledRef.current && filtered.length > 0) playSoundRef.current();

        } else if (data.type === 'lastSales') {
          console.log(`lastSales ${data.sales.length}`);
          setSales(filterSales(data.sales, filtersRef.current || []));

        } else if (data.type === 'newSales') {

          console.log(`newSales ${data.sales.length}`);
          const filtered = filterSales(data.sales, filtersRef.current || []);
          setSales(prev => [...prev, ...filtered]);
        }
        else if (data.type === 'oldSales') {
          const filtered = filterSales(data.sales, filtersRef.current || []);
          setSales(prev => [...filtered, ...prev]);

        } else if (data.type === 'remaining') {
          console.log(data.dataApp);
          setSales((prevSales) => {
            const remainingDataMap = new Map(data.dataApp.map(item => [item[0], { col2: item[1], sklad2: item[2], sizes: item[3] }]));
            return prevSales.map(sale => {
              if (remainingDataMap.has(sale.id)) {
                const { col2, sklad2, sizes } = remainingDataMap.get(sale.id);
                return { ...sale, col2, sklad2, sizes };
              }
              return sale;
            });
          });
        } else if (data.type === 'new_price') {
          console.log(data.dataApp);
          setSales((prevSales) => {
            const newPriceDataMap = new Map(data.dataApp.map(item => [item[0], { price2: item[1] }]));
            return prevSales.map(sale => {
              if (newPriceDataMap.has(sale.id)) {
                const { price2 } = newPriceDataMap.get(sale.id);
                return { ...sale, price2 };
              }
              return sale;
            });
          });
        } else if (data.type === 'new_feed') {
          console.log(data.dataApp);
          setSales((prevSales) => {
            const newPriceDataMap = new Map(data.dataApp.map(item => [item[0], { feedbackpoints2: item[1] }]));
            return prevSales.map(sale => {
              if (newPriceDataMap.has(sale.id)) {
                const { feedbackpoints2 } = newPriceDataMap.get(sale.id);
                return { ...sale, feedbackpoints2 };
              }
              return sale;
            });
          });
        }
      };


      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected.', event);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        const closeReason = String(event?.reason || '').toLowerCase();
        const isSubscriptionClosed = event.code === 1008
          && (closeReason.includes('subscription ended') || closeReason.includes('subscription is not active'));
        const isDeviceLimitClosed = event.code === 1008
          && closeReason.includes('too many devices');

        if (isSubscriptionClosed) {
          console.log('Subscription access ended.');
          setSubscriptionEnded(true);
          setShouldReconnect(false);
          setSales([]);
          setFeedbackSales([]);
          localStorage.removeItem('cachedSales');

        } else if (isDeviceLimitClosed) {
          setShouldReconnect(false);
          showGlassToast('Слишком много активных подключений. Закройте лишние вкладки или Safari/PWA дубли.', {
            id: WS_CONNECTION_TOAST_ID,
            variant: 'warning',
            duration: 5200,
          });
        } else if (event.code === 1008 && event.reason === 'logged in to another device') {
          setShouldReconnect(false);
          logoutRef.current()

        } else if (shouldReconnectRef.current && !document.hidden && !event.wasClean && reconnectAttempts.current < 3) {
          console.log('Attempting to reconnect...');
          reconnectAttempts.current += 1;
          reconnectTimeout.current = setTimeout(() => {
            void connectWebSocket();
          }, 1000);
        } else if (shouldReconnectRef.current && !event.wasClean) {
          setConnectionStatus('error');
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        ws.current.close();
        setIsConnected(false);
        setConnectionStatus('error');
      };
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (hiddenCloseTimeout.current) {
          clearTimeout(hiddenCloseTimeout.current);
        }
        hiddenCloseTimeout.current = setTimeout(() => {
          hiddenCloseTimeout.current = null;
          if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            console.log('Document hidden for a while, closing WebSocket.');
            ws.current.close(1000, 'hidden timeout');
          }
        }, 90000);
      } else {
        if (hiddenCloseTimeout.current) {
          clearTimeout(hiddenCloseTimeout.current);
          hiddenCloseTimeout.current = null;
        }
        console.log('Document visible, connecting WebSocket.');
        void connectWebSocket();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    void connectWebSocket();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setShouldReconnect(false);
      clearTimeout(reconnectTimeout.current);
      clearTimeout(hiddenCloseTimeout.current);
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [filtersReady, token, userInfo?.status, userInfo?.user_id]);

  useEffect(() => {
    const loadFeedback = async () => {
      if (!showFeedbackOnly || !token || !userInfo?.status || subscriptionEnded) return;
      setIsLoadingFeedback(true);
      try {
        const list = await getFeedbackSales({ limit: 500 });
        setFeedbackSales((list || []).slice().reverse());
      } catch (err) {
        console.error('Error loading feedback sales:', err);
      } finally {
        setIsLoadingFeedback(false);
      }
    };

    loadFeedback();
  }, [showFeedbackOnly, token, userInfo, getFeedbackSales, subscriptionEnded]);

  const sendJson = useCallback((payload) => {
    const socket = ws.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return false;
    }

    socket.send(typeof payload === 'string' ? payload : JSON.stringify(payload));
    return true;
  }, []);

  const addMessageListener = useCallback((listener) => {
    if (typeof listener !== 'function') {
      return () => {};
    }
    messageListeners.current.add(listener);
    return () => {
      messageListeners.current.delete(listener);
    };
  }, []);

  const fetchMoreSales = (firstId) => {
    return new Promise((resolve, reject) => {
      if (isFetchingRef.current) {
        reject('Already fetching');
        return;
      }

      if (sendJson({ type: 'getOldSales', id: firstId })) {
        isFetchingRef.current = true;
        resolve();
      } else {
        reject('WebSocket is not open');
      }
    }).finally(() => {
      isFetchingRef.current = false;
    });
  };

  const fetchMoreFeedbackSales = (firstId) => {
    return new Promise(async (resolve, reject) => {
      if (!firstId) {
        resolve();
        return;
      }

      if (feedbackFetchingRef.current) {
        reject('Already fetching feedback sales');
        return;
      }

      feedbackFetchingRef.current = true;
      try {
        const older = await getFeedbackSales({ beforeId: firstId, limit: 300 });
        const olderAsc = (older || []).slice().reverse();
        if (older && older.length > 0) {
          setFeedbackSales(prev => [...olderAsc, ...prev]);
        }
        resolve();
      } catch (err) {
        reject(err);
      } finally {
        feedbackFetchingRef.current = false;
      }
    });
  };


  useEffect(() => {
    const loadCachedData = async () => {
      if (userInfo?.status !== true || subscriptionEnded) {
        setSales([]);
        return;
      }
      const savedSales = localStorage.getItem('cachedSales');
      if (!savedSales) return;
      if (!filters) return;
      try {
        const parsedSales = JSON.parse(savedSales);
        console.log('Loaded from cache:', parsedSales.length);

        // Ждем загрузки фильтров перед фильтрацией
        while (!filtersLoaded.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const filtered = filterSales(parsedSales, filters);
        setSales(filtered.slice(-1000));
      } catch (error) {
        console.error('Error parsing cached sales:', error);
      }
    };

    loadCachedData();
  }, [filters, userInfo?.status, subscriptionEnded]); // Зависимость от filters

  useEffect(() => {
    const saveSales = () => {
      if (userInfo?.status !== true || subscriptionEnded) {
        localStorage.removeItem('cachedSales');
        return;
      }
      if (sales.length > 0) {
        const cachedSales = sales.slice(-1000);
        localStorage.setItem('cachedSales', JSON.stringify(cachedSales));
      } else {
        localStorage.removeItem('cachedSales');
      }
    };
    console.log(`sales length: ${sales.length}`);
    // Дебаунс на 1 секунду
    const saveDebounced = debounce(saveSales, 1000);

    // Сохраняем при изменении sales
    saveDebounced();

    // Отменяем отложенный вызов при размонтировании
    return () => saveDebounced.cancel();
  }, [sales, userInfo?.status, subscriptionEnded]);

  return (
    <WebSocketContext.Provider value={{
      ws: ws.current,
      isConnected,
      connectionStatus,
      connectionEpoch,
      subscriptionEnded,
      sales,
      feedbackSales,
      sendJson,
      addMessageListener,
      fetchMoreSales,
      fetchMoreFeedbackSales,
      isLoadingFeedback
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);

const ConnectionStatus = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isConnected, subscriptionEnded } = useWebSocket();
  const { userInfo } = useAuth();

  const isDark = theme === 'dark';
  const themeClass = theme === 'dark' ? 'text-white bg-dark' : 'text-dark bg-light';
  const wsShellClass = `pb-5 ws-notice-shell ${themeClass}`;
  const wsCardClass = `d-flex align-items-center justify-content-center text-center ${themeClass} ws-subscription-card ${isDark ? 'ws-subscription-card--dark' : 'ws-subscription-card--light'}`;
  const showConnectionNotice = !isConnected && userInfo?.status && !subscriptionEnded;

  useEffect(() => {
    if (showConnectionNotice) {
      showGlassToast('Устанавливаем связь с космосом...', {
        id: WS_CONNECTION_TOAST_ID,
        variant: 'status',
        duration: 0,
      });
    } else {
      hideGlassToast(WS_CONNECTION_TOAST_ID);
    }

    return () => hideGlassToast(WS_CONNECTION_TOAST_ID);
  }, [showConnectionNotice]);

  return (
    <>
      {subscriptionEnded && (
        <Container className={wsShellClass}>
          <Row className="mb-3">
            <Col>
              <Card className={wsCardClass}>
                <Card.Body>
                  <Card.Title className="ws-subscription-title">Подписка закончилась</Card.Title>
                  <Card.Text className="ws-subscription-text">Пожалуйста, оформите подписку, чтобы продолжить получать скидки.</Card.Text>
                  <Button onClick={() => navigate('/subscribe')} className="pay-primary-action ws-notice-pay-btn">
                    Оплатить подписку
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
      {!userInfo?.status && !subscriptionEnded && (
        <Container className={wsShellClass}>
          <Row className="mb-3">
            <Col>
              <Card className={wsCardClass}>
                <Card.Body>
                  <Card.Title className="ws-subscription-title">У вас неактивна подписка</Card.Title>
                  <Card.Text className="ws-subscription-text">Пожалуйста, оформите подписку, чтобы продолжить получать скидки.</Card.Text>
                  <Button onClick={() => navigate('/subscribe')} className="pay-primary-action ws-notice-pay-btn">
                    Оплатить подписку
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};

export default ConnectionStatus;
